import { useEffect } from "react";

import dayjs from "dayjs";
import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Dialog, DialogActions, DialogContent } from "@mui/material";
import Grid from "@mui/material/Grid";

import { ExpenseCategorySelect } from "../../expense-categories";
import {
  CenteredLoading,
  CustomDatePicker,
  CustomDialogTitle,
  CustomFileUpload,
  CustomInputField,
  CustomNumberField,
} from "../../ui";
import { DEFAULT_VALUES, VALIDATION_SCHEMA } from "./expense-form-constants";

export const ExpenseFormDialog = ({
  defaultValues,
  onSubmit,
  onClose,
  dialogTitle,
  renderDialogActionsChildren,
  isLoading = false,
}) => {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  useEffect(() => {
    if (defaultValues) reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = async (formData) =>
    await onSubmit({
      ...formData,
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      expenseCategoryId: formData.expenseCategory.id,
    });

  return (
    <Dialog
      component="form"
      fullWidth
      open
      onClose={handleClose}
      onSubmit={handleSubmit(onFormSubmit)}
    >
      <CustomDialogTitle
        variant="h5"
        title={dialogTitle}
        onClose={handleClose}
      />
      {isLoading ? (
        <CenteredLoading sx={{ height: "25rem" }} />
      ) : (
        <>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="transactionDate"
                  control={control}
                  render={({ field }) => (
                    <CustomDatePicker
                      value={field.value}
                      onChange={field.onChange}
                      required
                      label="Tanggal Transaksi"
                      error={Boolean(errors.transactionDate)}
                      helperText={errors.transactionDate?.message}
                      InputProps={{
                        disableFuture: true,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="expenseCategory"
                  render={({ field, fieldState }) => (
                    <ExpenseCategorySelect
                      value={field.value}
                      onChange={field.onChange}
                      TextFieldProps={{
                        required: true,
                        label: "Kategori",
                        error: fieldState.invalid,
                        helperText: fieldState.error?.message,
                        InputProps: {
                          placeholder: "Pilih",
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="amount"
                  render={({ field, fieldState }) => (
                    <CustomNumberField
                      value={field.value}
                      onChange={field.onChange}
                      required
                      label="Nominal"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <CustomInputField
                  name="note"
                  label="Catatan"
                  register={register}
                  helperText={errors.note?.message}
                  error={Boolean(errors.note)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomFileUpload
                  fullWidth
                  label="Lampiran"
                  onUploadSuccess={(path) => setValue("attachmentPath", path)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            {renderDialogActionsChildren({ isLoading: isSubmitting })}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

ExpenseFormDialog.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  renderDialogActionsChildren: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
